// Dependency-free line diff (LCS-based). Used for turn delta counts (+N -M)
// and the diff modal's hunks — both derived from the same real comparison
// between the previous Code.gs and the newly generated one.

function splitLines(text) {
  if (!text) return [];
  return text.replace(/\r\n/g, '\n').split('\n');
}

// Returns an array of { type: 'equal' | 'add' | 'remove', line }
export function diffLines(oldText, newText) {
  const a = splitLines(oldText);
  const b = splitLines(newText);
  const n = a.length;
  const m = b.length;

  // LCS length table
  const lcs = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: 'equal', line: a[i] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ type: 'remove', line: a[i] });
      i += 1;
    } else {
      ops.push({ type: 'add', line: b[j] });
      j += 1;
    }
  }
  while (i < n) {
    ops.push({ type: 'remove', line: a[i] });
    i += 1;
  }
  while (j < m) {
    ops.push({ type: 'add', line: b[j] });
    j += 1;
  }

  return ops;
}

export function diffCounts(ops) {
  let added = 0;
  let removed = 0;
  for (const op of ops) {
    if (op.type === 'add') added += 1;
    if (op.type === 'remove') removed += 1;
  }
  return { added, removed };
}

// Groups diff ops into hunks of contiguous changes with `context` lines of
// unchanged context on each side, matching the diff modal's "1 of N" shape.
export function diffHunks(ops, context = 1) {
  const changeIndexes = ops.reduce((acc, op, idx) => {
    if (op.type !== 'equal') acc.push(idx);
    return acc;
  }, []);
  if (!changeIndexes.length) return [];

  const ranges = [];
  let start = changeIndexes[0];
  let end = changeIndexes[0];
  for (let k = 1; k < changeIndexes.length; k++) {
    const idx = changeIndexes[k];
    if (idx - end <= context * 2 + 1) {
      end = idx;
    } else {
      ranges.push([start, end]);
      start = idx;
      end = idx;
    }
  }
  ranges.push([start, end]);

  return ranges.map(([s, e]) => {
    const from = Math.max(0, s - context);
    const to = Math.min(ops.length - 1, e + context);
    return ops.slice(from, to + 1);
  });
}
